import axios from "axios";
import { auth } from "../firebaseConfig/FirebaseConfig";

// Utility function to handle API requests
const apiRequest = async (url, method, data = {}, params = {}) => {
  try {
    const config = { method, url, data, params };
    const response = await axios(config);
    console.log("api response", response);
    if (response?.status === 200) {
      console.log("api data response", response);
      return response.data;
    }
    throw new Error("Failed to get payment session ID");
  } catch (error) {
    throw new Error(
      error?.response?.data?.error ||
        "An error occurred while processing your request."
    );
  }
};

// Validate email
export const validateEmail = async (email) => {
  try {
    const data = await apiRequest(process.env.REACT_APP_API_VAl_EM, "post", {
      email,
    });
    return data?.exists || false;
  } catch (error) {
    throw new Error(error?.response?.data?.error || "Failed to validate email");
  }
};

// Handle payment process
export const paymentMethod = async (name, number, email, plan) => {
  try {
    const response = await apiRequest(process.env.REACT_APP_API_PAY, "post", {
      name,
      number,
      email,
      plan,
    });

    if (!response) {
      return false; // Explicitly return false on failure
    }

    const { paymentGatewayUrl, formData } = response;
    if (!paymentGatewayUrl || !formData) {
      return false;
    }

    // ✅ Create form dynamically
    const form = document.createElement("form");
    form.method = "POST";
    form.action = paymentGatewayUrl;

    Object.entries(formData).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit(); // Redirects user to payment gateway

    return true; // Not necessary, but included for clarity
  } catch (error) {
    return false; // Explicit failure response
  }
};

// Verify payment status
export const verifyPayment = async (txnid) => {
  try {
    const response = await apiRequest(
      process.env.REACT_APP_API_PAY_STATUS,
      "post",
      {
        txnid,
      }
    );
    if (response) {
      return response.payment;
    }
  } catch (error) {
    throw new Error(error?.response?.data?.error || "Failed to validate email");
  }
};

export const activateSubscription = async ({ user, plan }) => {
  try {
    if (!user || !plan) {
      throw new Error("Invalid user");
    }

    const { name, number, email } = user;

    // Step 1: Start the payment process
    const paymentInitiated = await paymentMethod(name, number, email, plan);

    if (!paymentInitiated) {
      throw new Error("Payment initiation failed");
    }

    // Step 2: Inform user to check payment status (Webhook handles everything)
    console.log("Payment initiated. Redirecting to PayU...");

    return {
      success: true,
      message: "Payment initiated. Please complete the payment on PayU.",
    };
  } catch (error) {
    throw new Error(
      error?.response?.data?.error ||
        "Failed to initiate subscription renewal. Please try again."
    );
  }
};

export const signUp = async ({ name, number, email, password }) => {
  try {
    const response = await apiRequest(
      process.env.REACT_APP_API_SIGNUP,
      "post",
      {
        name,
        number,
        email,
        password,
      }
    );

    if (response?.message === "User created successfully") {
      await signIn({ email, password });
      return response.user;
    } else {
      throw new Error("Signup failed");
    }
  } catch (error) {
    throw new Error(
      error?.response?.data?.error || "Failed to sign up. Please try again."
    );
  }
};

export const fetchUserData = async (firebaseUser) => {
  try {
    const idToken = await firebaseUser.getIdToken(true); // Force token refresh
    const response = await apiRequest(
      process.env.REACT_APP_API_SIGNIN,
      "post",
      {
        token: idToken,
      }
    );

    if (response?.user) {
      return response.user; // Return user data from the API
    } else {
      throw new Error("Failed to fetch user data.");
    }
  } catch (error) {
    throw new Error(
      error?.response?.data?.error || "Unable to fetch user data."
    );
  }
};

// export const generatePoster = async (posterData) => {
//   try {
//     const response = await apiRequest(
//       process.env.REACT_APP_API_GENERATE_POSTER,
//       "POST",
//       posterData // Send the full user & offer details
//     );

//     const data = await response;
//     if (data.success) {
//       console.log("Poster URL:", data.posterUrl);
//       return data.posterUrl; // Return Firebase Storage URL
//     } else {
//       throw new Error("Failed to generate poster.");
//     }
//   } catch (error) {
//     console.error("Error generating poster:", error);
//     throw new Error("Error generating poster. Please try again.");
//   }
// };

export const signIn = async ({ email, password }) => {
  try {
    console.log("signin started", email, password);
    const userCredential = await auth?.signInWithEmailAndPassword(
      email,
      password
    );
    if (userCredential) {
      console.log("user exists");
      return userCredential.user;
    } else {
      console.log("user not exists");
      throw new Error("Signin failed");
    }
  } catch (error) {
    console.log("error here", error);
    throw new Error(
      error?.response?.data?.error || "Failed to sign in. Please try again."
    );
  }
};

export const activateAdminSubscription = async ({
  firebaseUser,
  uid,
  subscriptionPlan,
}) => {
  try {
    if (!firebaseUser || !uid || !subscriptionPlan) {
      throw new Error("Missing required fields");
    }

    const token = await firebaseUser.getIdToken(); // Get Firebase token

    const response = await apiRequest(
      process.env.REACT_APP_API_ACTIVATE_SUBSCRIPTION,
      "post",
      { uid, subscriptionPlan, token }
    );

    return response;
  } catch (error) {
    throw new Error(error.message || "Subscription activation failed");
  }
};

export const signOut = async () => {
  try {
    await auth.signOut();
  } catch (error) {
    throw new Error(
      error?.response?.data?.error || "Failed to sign out. Please try again."
    );
  }
};

export const resetPassword = async ({ email }) => {
  try {
    await auth.sendPasswordResetEmail(email);
    return true;
  } catch (error) {
    throw new Error("Failed to reset password. Please try again.");
  }
};
